import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, Plus, Trash2 } from "lucide-react";

export default function PanelMembersPanel({
  members,
  newMember,
  setNewMember,
  showDialog,
  setShowDialog,
  onAdd,
  onRemove,
}) {
  return (
    <Card data-testid="panel-members-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Interview Panel Members
            </CardTitle>
            <CardDescription>Interviewers who will score F2F candidates</CardDescription>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-panel-member-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Panel Member</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    placeholder="John Doe"
                    data-testid="panel-member-name-input"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    placeholder="john@securado.net"
                    data-testid="panel-member-email-input"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Input
                    value={newMember.role}
                    onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                    placeholder="Technical Interviewer"
                    data-testid="panel-member-role-input"
                  />
                </div>
                <div className="border-t pt-4 mt-2">
                  <p className="text-sm font-medium mb-3 text-gray-700">
                    📅 Optional — Schedule interview & send calendar invite
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Interview date & time</Label>
                      <Input
                        type="datetime-local"
                        value={newMember.interview_at || ""}
                        onChange={(e) => setNewMember({ ...newMember, interview_at: e.target.value })}
                        data-testid="panel-member-interview-at-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Duration (min)</Label>
                      <Input
                        type="number"
                        min={15}
                        max={240}
                        step={15}
                        value={newMember.interview_duration_minutes || 60}
                        onChange={(e) => setNewMember({ ...newMember, interview_duration_minutes: parseInt(e.target.value, 10) || 60 })}
                        data-testid="panel-member-duration-input"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs">Candidate name (shown on invite)</Label>
                    <Input
                      value={newMember.candidate_name || ""}
                      onChange={(e) => setNewMember({ ...newMember, candidate_name: e.target.value })}
                      placeholder="e.g. Jane Smith"
                      data-testid="panel-member-candidate-name-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Leave blank to invite without a specific time — you can schedule later.
                  </p>
                </div>
                <Button onClick={onAdd} className="w-full" data-testid="panel-member-submit-btn">
                  Add Panel Member
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {members.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No panel members added yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {members.map((member) => (
              <div key={member.email} className="flex items-center justify-between p-4 border rounded-lg bg-white" data-testid={`panel-member-${member.email}`}>
                <div>
                  <h4 className="font-medium">{member.name}</h4>
                  <p className="text-sm text-gray-600">{member.email}</p>
                  <Badge variant="outline" className="mt-1">{member.role}</Badge>
                  {member.interview_at && (
                    <p className="text-xs text-green-700 mt-2">
                      📅 {new Date(member.interview_at).toLocaleString()} · {member.interview_duration_minutes || 60} min
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(member.email)}
                  data-testid={`remove-panel-member-${member.email}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
